import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PackageData } from 'types/Product';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  amount: number;
  type?: 'product' | 'package';
  products?: string[];
  requires_cleaning_deposit: boolean
}

export interface CartState {
  isEdit: boolean;
  items: CartItem[];
  bookingDate: string | null;
  package: PackageData | null;
  isPackageItem: boolean;
  vat: number;
  cleaningDeposit: number;
  cleaningDepositAmount: number
  updatedDepositAmount: number
  subTotal: number;
  total: number;
  paid_amount: number;
  paymentMethod: 'cash' | 'stripe' | null;
  stripeConfig: {
    publicKey: string | null;
    isEnabled: boolean;
  };  
  payment: {
    isProcessing: boolean;
    isSuccessful: boolean;
    error: string | null;
    token: string | null;
    status_id: string | null;
  };
  discountAmount: number;
  discountCode: string | null;
  isDepositEnabled: boolean;
  depositAmount: number;
  depositCollected: boolean;
}

// Helper function to calculate totals
const calculateTotals = (items: CartItem[]) => {
  const subTotal = items.reduce((sum, item) => {
    const price = parseFloat(item.price?.toString() || '0');
    const amount = parseInt(item.amount?.toString() || '0');
    return sum + (price * amount);
  }, 0);
  return { subTotal };
};

const initialState: CartState = {
  isEdit: false,
  items: [],
  package: null,
  isPackageItem: false,
  bookingDate: null,
  vat: 0,
  cleaningDeposit: 0, //calculated amount -- if product requires, it will be added
  cleaningDepositAmount: 0, //amount set by admin
  updatedDepositAmount: 0, //in case of edit, if booking payload has 0, we can use new/updated deposit value in edit 
  subTotal: 0,
  total: 0,
  paid_amount: 0,
  paymentMethod: null,
  stripeConfig: {
    publicKey: null,
    isEnabled: false
  },  
  payment: {
    isProcessing: false,
    isSuccessful: false,
    error: null,
    token: null,
    status_id: null
  },
  discountAmount: 0,
  discountCode: null,
  isDepositEnabled: false,
  depositAmount: 0,
  depositCollected: false,
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    setItems: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
      const totals = calculateTotals(state.items);
      // console.log(action.payload)
      state.cleaningDeposit = action.payload.some(item => item.requires_cleaning_deposit) ? state.cleaningDepositAmount : 0
      state.subTotal = totals.subTotal;
    },
    setBookingDate: (state, action: PayloadAction<string>) => {
      state.bookingDate = action.payload;
    },
    setIsPackageItem(state: CartState, action: PayloadAction<boolean>) {
      state.isPackageItem = action.payload;
      state.cleaningDeposit = action.payload ? state.cleaningDepositAmount : 0
    },
    setPackageData(state: CartState, action: PayloadAction<PackageData | null>) {
      state.package = action.payload;
    },
    setTotal: (state, action: PayloadAction<{total : number}>) => {
      state.total = action.payload.total;
    },
    setTaxesAndDeposits: (state, action: PayloadAction<{ vat: number; cleaningDeposit: number, updatedCleaningDeposit? : number, deposit: number, isDepositEnabled: boolean }>) => {
      state.vat = action.payload.vat;
      state.cleaningDepositAmount = action.payload.cleaningDeposit;
      if(action.payload?.updatedCleaningDeposit){state.updatedDepositAmount = action.payload?.updatedCleaningDeposit || 0;}
      state.isDepositEnabled = action.payload.isDepositEnabled;
    },
    setCleaningDeposit:(state, action: PayloadAction<{hasCleaningDeposit: boolean }>) => {
      // console.log('need deposit', action.payload.hasCleaningDeposit)
      if(action.payload.hasCleaningDeposit){
        state.cleaningDeposit = state.cleaningDepositAmount > 0 ? state.cleaningDepositAmount : state.updatedDepositAmount;
      }
    },
    setStripeConfig: (state, action: PayloadAction<{ publicKey: string; isEnabled: boolean }>) => {
      state.stripeConfig = action.payload;
    },
    setPaymentMethod(state: CartState, action: PayloadAction<{method :'cash' | 'stripe'  | null, depositCollected?: boolean}>) {
      state.paymentMethod = action.payload.method;
      state.depositCollected = action.payload.depositCollected || false;
    },
    setPaymentProcessing: (state, action: PayloadAction<boolean>) => {
      state.payment.isProcessing = action.payload;
    },    
    setPaymentStatus: (state, action: PayloadAction<{
      isSuccessful?: boolean;
      error?: string | null;
      token?: string | null;
      status_id?: string | null;
    }>) => {
      state.payment = {
        ...state.payment,
        isSuccessful: action.payload.isSuccessful || state.payment.isSuccessful,
        error: action.payload.error || state.payment.error,
        token: action.payload.token || state.payment.token,
        status_id: action.payload.status_id || state.payment.status_id,
        isProcessing: false
      };
    },

    clearCart: () => initialState,
    setInitialCartState: (state, action) => {
      const { items, bookingDate, total, paymentMethod, payment, 
              discountAmount, discountCode, depositAmount, depositCollected, 
              isPackageItem, AddedPackage, paid_amount } = action.payload;
      state.items = items;
      const totals = calculateTotals(items);
      state.subTotal = totals.subTotal;
      state.bookingDate = bookingDate;
      state.total = total;
      state.paymentMethod = paymentMethod;
      state.payment = {...state.payment, status_id: payment};
      state.discountAmount = discountAmount || 0;
      state.discountCode = discountAmount > 0 ? discountCode : null;
      state.depositAmount = depositAmount || 0;
      state.depositCollected = depositCollected || false;
      state.isPackageItem = isPackageItem || false;
      state.package = AddedPackage || null; 
      state.paid_amount = paid_amount || 0;
    },
    setDiscountAmount(state: CartState, action: PayloadAction<{
      discountAmount: number;
      discountCode: string | null;
    }>) {
      state.discountAmount = action.payload.discountAmount;
      state.discountCode = action.payload.discountAmount > 0 ? action.payload.discountCode : null;
    }
  }
});

// Export actions
export const {
  setEditMode,
  setItems,
  setIsPackageItem,
  setPackageData,
  setBookingDate,
  setTaxesAndDeposits,
  setCleaningDeposit,
  setStripeConfig,
  setPaymentMethod,
  setPaymentProcessing,
  setPaymentStatus,
  clearCart,
  setTotal,
  setDiscountAmount,
  setInitialCartState,
} = cartSlice.actions;

// Selectors
export const selectIsEdit = (state: { cart: CartState }) => state.cart.isEdit;
export const selectCartItems = (state: { cart: CartState }) => state.cart.items;
export const selectBookingDate = (state: { cart: CartState }) => state.cart.bookingDate;
export const selectCartTotals = (state: { cart: CartState }) => ({
  subTotal: state.cart.subTotal,
  vat: state.cart.vat,
  cleaningDeposit: state.cart.cleaningDeposit,
  total: state.cart.total, 
  discountAmount: state.cart.discountAmount,
  depositAmount: state.cart.depositAmount,
  depositCollected: state.cart.depositCollected,
});
export const selectPaymentMethod = (state: { cart: CartState }) => state.cart.paymentMethod;
export const selectStripeConfig = (state: { cart: CartState }) => state.cart.stripeConfig;
export const selectPaymentStatus = (state: { cart: CartState }) => state.cart.payment;
export const selectCart = (state: { cart: CartState }) => state.cart;
export const selectIsDiscountedBooking = (state: { cart: CartState }) => {
  return state.cart.isEdit && state.cart.discountAmount > 0;
}
export const isPackageItem = (state: { cart: CartState }) => state.cart.isPackageItem;
export const selectPackageData = (state: { cart: CartState }) => state.cart.package;
export const selectPaidAmount = (state: { cart: CartState }) => state.cart.paid_amount;

export default cartSlice.reducer;